import React, { useState, useEffect, useRef } from 'react';
import { Alert, AlertTitle } from '@/components/ui/alert';
import { Truck, Loader2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuthContext } from '@/components/auth-context/auth-context';
import { QRCodeSVG } from 'qrcode.react';
import styles from './check-identity.module.scss';
import cognitoConfig from '../../config/cognito';
import { initiateKYC } from '@/use-cases/initiate-kyc';
import notifyHB from '@/config/notifyHB';
import { getClientDeviceType } from '@/utils/get-client-device-type';
import routeConfig from '@/config/routeConfig';
import { useHistory } from 'react-router-dom';

interface RegisterProps {
	prevStep: () => void;
	canGoBack: boolean;
}

const CheckIdentity: React.FC<RegisterProps> = ({ prevStep, canGoBack }) => {
	const { kycUrl, setKycUrl } = useAuthContext();
	const history = useHistory();
	const ref = useRef<HTMLDivElement>(null);
	const [isRecoveringKyc, setIsRecoveringKyc] = useState(false);

	const cleanStorage = () => {
		setKycUrl(null);
		localStorage.removeItem('onboarding-step');
		localStorage.removeItem('onboarding-ulid');
		localStorage.removeItem('onboard');
		localStorage.removeItem('checkout-id');
		localStorage.removeItem('checkout-url');
	};

	useEffect(() => {
		const svgs = ref.current?.querySelectorAll('svg');
		if (svgs) {
			const svg = Array.from(svgs).filter((svg) =>
				svg.hasAttribute('role'),
			)[0];

			svg.removeAttribute('width');
			svg.removeAttribute('height');
			svg.setAttribute('width', '40%');
			svg.setAttribute('height', 'auto');
		}
	}, []);

	useEffect(() => {
		if (!kycUrl) {
			const ulid = localStorage.getItem('onboarding-ulid');
			if (ulid) {
				setIsRecoveringKyc(true);
				initiateKYC(ulid, cognitoConfig.cognitoClient)
					.then((res) => setKycUrl(res.kyc_url))
					.catch((err) => notifyHB('request-signature', 'CheckIdentity', err))
					.finally(() => setIsRecoveringKyc(false));
			}
		}
	}, []);

	return (
		<div className={styles['check-identity']}>
			{isRecoveringKyc && (
				<div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
					<Loader2Icon className="animate-spin" />
				</div>
			)}
			<span className={styles.title}>Comprueba tu identidad</span>
			{kycUrl &&
				(getClientDeviceType() !== 'desktop' ? (
					<Button
						onClick={() =>
							window.open(kycUrl, '_blank', 'noopener,noreferrer')
						}
					>
						Continua en una nueva pestaña
					</Button>
				) : (
					<>
						<Alert variant="default" className={styles['alert']}>
							<Truck color={'#458EF3'} />
							<AlertTitle className={styles['alert-title']}>
								Para continuar con la verificación escanea este
								QR con tu celular. Necesitarás grabar selfies y
								compartir fotos de tu DPI.
							</AlertTitle>
						</Alert>
						<div className={styles.qr} ref={ref}>
							{<QRCodeSVG value={kycUrl} />}
							<p>
								¿Estás usando tu celular? Continúa{' '}
								<a
									target="_blank"
									rel="noopener noreferrer"
									href={
										kycUrl.startsWith('https://')
											? kycUrl
											: `https://${kycUrl}`
									}
								>
									aquí
								</a>
							</p>
						</div>
					</>
				))}
			<div className={styles.footer}>
				{canGoBack && (
					<Button variant="secondary" onClick={prevStep}>
						Regresar
					</Button>
				)}
				<Button
					onClick={() => {
						cleanStorage();
						const pendingSigningRequestDocUlid =
							localStorage.getItem(
								'pending-signing-request-doc-ulid',
							);
						if (pendingSigningRequestDocUlid) {
							localStorage.removeItem(
								'pending-signing-request-doc-ulid',
							);
							history.push(routeConfig.mySignatreRequests);
						} else {
							history.push(routeConfig.dashboard);
						}
					}}
				>
					Continuar
				</Button>
			</div>
		</div>
	);
};

export default CheckIdentity;
